class BlankaboxExperience extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleEditionMediaClick = this.handleEditionMediaClick.bind(this);
    this.handleFabricInteraction = this.handleFabricInteraction.bind(this);
    this.animateFabricRail = this.animateFabricRail.bind(this);

    this.setupFabricRail();
    this.addEventListener('click', this.handleEditionMediaClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleEditionMediaClick);
    this.fabricRail?.removeEventListener('pointerdown', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('pointerup', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('pointercancel', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('touchstart', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('touchend', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('wheel', this.handleFabricInteraction);
    this.fabricVisibilityObserver?.disconnect();
    if (this.fabricAutoFrame) cancelAnimationFrame(this.fabricAutoFrame);
  }

  handleEditionMediaClick(event) {
    if (window.innerWidth > 749) return;

    const media = event.target.closest('[data-bbx-edition-media]');
    if (!media || !this.contains(media)) return;

    const isAlternateVisible = media.classList.toggle('is-alternate-visible');
    media.setAttribute('aria-pressed', String(isAlternateVisible));
  }

  setupFabricRail() {
    this.fabricRail = this.querySelector('[data-bbx-fabric-rail]');
    if (!this.fabricRail || this.reducedMotion.matches) return;

    this.fabricDirection = 1;
    this.fabricAutoPosition = this.fabricRail.scrollLeft;
    this.fabricWasPaused = true;
    this.fabricVelocity = 0;
    this.fabricIsVisible = true;
    this.fabricPausedUntil = performance.now() + 1200;
    this.fabricLastTimestamp = null;
    this.fabricRail.addEventListener('pointerdown', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('pointerup', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('pointercancel', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('touchstart', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('touchend', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('wheel', this.handleFabricInteraction, { passive: true });

    if ('IntersectionObserver' in window) {
      this.fabricIsVisible = false;
      this.fabricVisibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.fabricIsVisible = entry?.isIntersecting === true;
        },
        { rootMargin: '120px 0px' }
      );
      this.fabricVisibilityObserver.observe(this.fabricRail);
    }

    this.fabricAutoFrame = requestAnimationFrame(this.animateFabricRail);
  }

  handleFabricInteraction(event) {
    const interactionStarted = event.type === 'pointerdown' || event.type === 'touchstart';
    this.fabricPausedUntil = interactionStarted ? Number.POSITIVE_INFINITY : performance.now() + 1800;
    this.fabricAutoPosition = this.fabricRail?.scrollLeft || 0;
    this.fabricWasPaused = true;
    this.fabricVelocity = 0;
  }

  animateFabricRail(timestamp) {
    if (!this.fabricRail) return;

    if (this.fabricLastTimestamp === null) this.fabricLastTimestamp = timestamp;
    const elapsed = Math.min(timestamp - this.fabricLastTimestamp, 40);
    this.fabricLastTimestamp = timestamp;

    const fabricInteracting = this.fabricRail.matches(':hover') || this.fabricRail.contains(document.activeElement) || this.fabricRail.querySelector('.is-model-visible');
    if (this.fabricIsVisible && !fabricInteracting && timestamp >= this.fabricPausedUntil) {
      const maxScroll = Math.max(this.fabricRail.scrollWidth - this.fabricRail.clientWidth, 0);
      const targetSpeed = window.innerWidth <= 749 ? 34 : 40;

      if (maxScroll > 0) {
        if (this.fabricWasPaused) {
          this.fabricAutoPosition = this.fabricRail.scrollLeft;
          this.fabricWasPaused = false;
        }

        const acceleration = Math.min(elapsed / 850, 1);
        this.fabricVelocity += (targetSpeed - this.fabricVelocity) * acceleration;
        this.fabricAutoPosition += this.fabricDirection * this.fabricVelocity * (elapsed / 1000);

        if (this.fabricAutoPosition >= maxScroll) {
          this.fabricAutoPosition = maxScroll;
          this.fabricDirection = -1;
          this.fabricVelocity = 0;
        } else if (this.fabricAutoPosition <= 0) {
          this.fabricAutoPosition = 0;
          this.fabricDirection = 1;
          this.fabricVelocity = 0;
        }

        this.fabricRail.scrollLeft = this.fabricAutoPosition;
      }
    } else {
      this.fabricWasPaused = true;
      this.fabricVelocity = 0;
    }

    this.fabricAutoFrame = requestAnimationFrame(this.animateFabricRail);
  }
}

if (!customElements.get('blankabox-experience')) {
  customElements.define('blankabox-experience', BlankaboxExperience);
}
